import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { ButtonType, TinyDownloadButtonsProps } from "@/types/ui-kit/buttons";
import { DialogModalWindow } from "@/components/modules/Post/DialogModalWindow";
import { BaseButton } from "@/components/ui-kit/Buttons/BaseButton";
import { useAuthUrls } from "@/hooks/useAuthUrls";
import { FEATURE_PERMISSIONS } from "@/core/types/permissions";
import { BaseTooltip } from "@/components/ui-kit/Buttons/BaseTooltip";
import autoAnimate from "@formkit/auto-animate";
import useSubscriptionPermissionsChecker from "@/hooks/useSubscriptionPermissionsChecker";
import { UseSubsPermissionsActionTypes } from "@/types/hooks";
import { useIsAppOnline } from "@/hooks/useIsAppOnline";
import { pushToDataLayer } from "@/utils/pushToDataLayer";
import useUserProvider from "@/core/dataProviders/providers/auth/useUserProvider";

export const TinyDownloadButton: React.FC<TinyDownloadButtonsProps> = ({
  onClick,
  short,
  downloaded = false,
  downloading = false,
  issuesTooltipTitle,
  postTitle,
  mediaURL,
}) => {
  const [user] = useUserProvider();
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const parent = useRef(null);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const isAppOnline = useIsAppOnline();
  const { getSubscriptionUrl } = useAuthUrls();
  const isFeaturePermission = useSubscriptionPermissionsChecker(
    UseSubsPermissionsActionTypes.INVALID_PERMISSIONS,
    FEATURE_PERMISSIONS.DOWNLOADS
  );

  useEffect(() => {
    parent.current && autoAnimate(parent.current);
  }, [parent]);

  useEffect(() => {
    setIsDownloading(downloading);
  }, [downloading]);

  useEffect(() => {
    setIsDownloaded(downloaded);
  }, [downloaded]);

  const getDownloadTooltipTitle =
    (isDownloaded || isDownloading) && !issuesTooltipTitle
      ? "Odebrat ze stažených"
      : (isDownloaded || isDownloading) && issuesTooltipTitle
        ? "Odebrat celé vydání"
        : issuesTooltipTitle
          ? "Stáhnout celé vydání"
          : "Stáhnout";

  return (
    <>
      <button
        onClick={() => {
          if (isAppOnline && isFeaturePermission) {
            setIsDialogVisible(true);
            return;
          }
          setIsHovered(false);
          setIsDownloaded(!isDownloaded);
          onClick?.();
          pushToDataLayer({
            event: "article_interaction",
            interaction_type: "download issue",
            interaction_option: postTitle ?? "Magazín Reportér",
            media_url: mediaURL ?? undefined,
          });
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label="Stáhnout"
        ref={parent}
        className={`relative flex items-center justify-center rounded-full border border-gray-300 bg-white dark:border-white-50 dark:bg-dark ${
          short ? "w-[36px]" : "w-[36px] md:w-[50px]"
        } py-1 transition-colors ease-in-out`}
      >
        <BaseTooltip visible={isHovered} content={getDownloadTooltipTitle} />
        {user && (isDownloaded || isDownloading) ? (
          <Image
            src="/assets/images/icons/new-downloaded.svg"
            alt="Download icon"
            className="h-[16px] w-[16px]"
            width={16}
            height={16}
            aria-hidden={true}
          />
        ) : isHovered ? (
          <Image
            src="/assets/images/icons/download-navigation-hovered.svg"
            alt="Download icon"
            className="h-[16px] w-[16px]"
            width={16}
            height={16}
            aria-hidden={true}
          />
        ) : (
          <Image
            src="/assets/images/icons/download-navigation.svg"
            alt="Download icon"
            className="h-[16px] w-[16px] dark:invert"
            width={16}
            height={16}
            aria-hidden={true}
          />
        )}
      </button>
      <DialogModalWindow
        title="Chcete mít váš oblíbený obsah dostupný, i když jste bez internetového připojení?"
        text="Aktivujte si předplatné pro stahování celých magazínových vydání, jednotlivých článků nebo podcastů."
        button={<BaseButton href={getSubscriptionUrl()} title="Chci předplatné" type={ButtonType.Primary} />}
        label="Pokud už předplatné máte"
        isOpen={isDialogVisible}
        onClose={() => setIsDialogVisible(false)}
      />
    </>
  );
};
